import React, { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { Header, SiteFooter } from "./primitives";
import { BlockLogoSplash, BlockHero, BlockWaarom, BlockVideo, BlockOnderscheiding } from "./blocks-a";
import { BlockWinnaars, BlockGalerij, BlockVerslag, BlockAftermovie } from "./blocks-b";
import {
  BlockBestuur, BlockGala, BlockPlek, BlockSignup, BlockPartners, FotogalerieOverlay,
} from "./blocks-c";
import { BlockTekst, BlockFotoblok } from "./blocks-extra";

function byId(list, key = "id") {
  return (list || []).reduce((acc, item) => { acc[item[key]] = item; return acc; }, {});
}

export default function SitePage({ concept = false }) {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.classList.add("swof");
    document.body.dataset.motion = "low";
    return () => document.body.classList.remove("swof");
  }, []);

  useEffect(() => {
    api.get(concept ? "/admin/site" : "/public/site")
      .then(({ data }) => setContent(data))
      .catch(() => setError("De inhoud kon niet worden geladen."));
  }, [concept]);

  const derived = useMemo(() => {
    if (!content) return null;
    const fotosByInductee = {};
    (content.inductee_fotos || []).forEach((f) => {
      (fotosByInductee[f.inductee_id] = fotosByInductee[f.inductee_id] || []).push(f);
    });
    return {
      mediaById: byId(content.media),
      videosById: byId(content.videos),
      fotosByInductee,
    };
  }, [content]);

  useEffect(() => {
    if (content?.page?.titel) document.title = content.page.titel;
  }, [content]);

  if (error) {
    return <div style={{ padding: 80, fontFamily: "var(--serif)", color: "var(--cream)" }}>{error}</div>;
  }
  if (!content) {
    return <div style={{ minHeight: "100vh", background: "var(--ink)" }} data-testid="site-loading" />;
  }

  const visible = content.blocks.filter((b) => b.zichtbaar);
  const scrollBlocks = visible.filter((b) => b.type !== "fotogalerie");
  const fotogalerie = visible.find((b) => b.type === "fotogalerie");
  const show = visible.reduce((acc, b) => { acc[b.type] = true; return acc; }, {});
  const galerij_fotos = content.galerij_fotos || [];

  const render = (b) => {
    const inhoud = b.inhoud;
    switch (b.type) {
      case "logo_splash":
        return <BlockLogoSplash key={b.id} inhoud={inhoud} logo={derived.mediaById[inhoud.logo_id]} />;
      case "hero":
        return <BlockHero key={b.id} inhoud={inhoud} />;
      case "waarom":
        return <BlockWaarom key={b.id} inhoud={inhoud} />;
      case "video":
        return <BlockVideo key={b.id} inhoud={inhoud} video={derived.videosById[inhoud.video_id]} />;
      case "onderscheiding":
        return <BlockOnderscheiding key={b.id} inhoud={inhoud} />;
      case "winnaars":
        return (
          <BlockWinnaars key={b.id} inhoud={inhoud} inductees={content.inductees}
            mediaById={derived.mediaById} videosById={derived.videosById}
            fotosByInductee={derived.fotosByInductee} />
        );
      case "galerij":
        return (
          <BlockGalerij key={b.id} inhoud={inhoud} fotos={galerij_fotos}
            mediaById={derived.mediaById} fotogalerieAan={Boolean(fotogalerie)} />
        );
      case "verslag":
        return <BlockVerslag key={b.id} inhoud={inhoud} />;
      case "aftermovie":
        return <BlockAftermovie key={b.id} inhoud={inhoud} video={derived.videosById[inhoud.video_id]} />;
      case "bestuur":
        return <BlockBestuur key={b.id} inhoud={inhoud} bestuursleden={content.bestuursleden} mediaById={derived.mediaById} />;
      case "gala":
        return <BlockGala key={b.id} inhoud={inhoud} />;
      case "plek":
        return <BlockPlek key={b.id} inhoud={inhoud} />;
      case "signup":
        return <BlockSignup key={b.id} inhoud={inhoud} />;
      case "partners":
        return <BlockPartners key={b.id} inhoud={inhoud} partners={content.partners} mediaById={derived.mediaById} />;
      case "tekst":
        return <BlockTekst key={b.id} inhoud={inhoud} />;
      case "fotoblok":
        return <BlockFotoblok key={b.id} inhoud={inhoud} mediaById={derived.mediaById} />;
      default:
        return null;
    }
  };

  return (
    <div id="top" data-testid="swof-site" style={{ background: "var(--ink)" }}>
      {concept && (
        <div data-testid="concept-balk" style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900,
          background: "var(--gold)", color: "var(--ink)", textAlign: "center",
          fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.2em",
          textTransform: "uppercase", padding: "10px 16px",
        }}>Voorbeeld · concept-inhoud</div>
      )}
      <Header show={show} />
      {scrollBlocks.map(render)}
      {fotogalerie && (
        <FotogalerieOverlay inhoud={fotogalerie.inhoud} fotos={galerij_fotos} mediaById={derived.mediaById} />
      )}
      <SiteFooter />
    </div>
  );
}
